import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { PopoverConfig, PopoverRef, PopoverService } from '@mandae/ng-popover';
import { iif, Observable, of, Subject, throwError } from 'rxjs';
import { delay, map, mergeMap, retry, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UiRouterState } from '../services/ajs-router-state.service';
import { RouterTransitionsService } from '../services/ajs-router-transitions.service';
import { StateParamsService } from '../services/ajs-state-params.service';
import { TourStep } from './tour-step';

export interface TourConfig {
  steps: TourStep[];
}

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private transitionEnd = new Subject();

  constructor(
    private popoverService: PopoverService,
    private router: UiRouterState,
    private stateParams: StateParamsService,
    private routerTransitionsService: RouterTransitionsService,
    @Inject(DOCUMENT) private doc: Document,
  ) {
    this.routerTransitionsService.onSuccess({}, (transition) => this.transitionEnd.next(transition));
  }

  public makeTour(steps: TourStep[]) {
    const makeButtons = (index: number, step: TourStep) => {
      const makeButton = (text, flat, click) => ({ text, flat, click });

      const buttons = [];
      if (step.previous) {
        buttons.push(makeButton(step.previous.text, false, step.previous.action));
      } else if (index !== 0) {
        buttons.push(makeButton('Voltar', false, (ev, ref: PopoverRef) => ref.close('back')));
      }

      if (step.next) {
        buttons.push(makeButton(step.next.text, true, step.next.action));
      } else {
        const isLastStep = index === steps.length - 1;
        const text = isLastStep ? 'Fechar' : 'Avançar';
        buttons.push(makeButton(text, true, (ev, ref: PopoverRef) => ref.close('next')));
      }

      return buttons;
    };

    // Map the TourStep to the popover settings
    return steps.map((step, index) => {
      const buttons = makeButtons(index, step);

      return {
        preferredPosition: step.preferredPosition,
        attachTo: step.attachTarget,
        disposeOnNavigation: true,
        data: {
          backgroundColor: '#2A2A2A',
          foregroundColor: '#FFFFFF',
          content: step.text,
          title: step.title,
          buttons,
          close: (ref: PopoverRef) => ref.close(),
        }
      } as PopoverConfig;
    });
  }

  public start(config: PopoverConfig[]) {
    return this.openPopover(config, 0).pipe(
      tap(ref => {
        if (!ref || !ref.closed) {
          return;
        }

        ref.closed.subscribe(result => {
          if (!result) {
            const params = { ...this.stateParams };
            delete params.tour;
            this.router.go(this.router.current.name, params, { inherit: false });
          }
        });
      })
    );
  }

  private openPopover(config: PopoverConfig[], step: number): Observable<PopoverRef> {
    if (!config[step]) {
      return throwError(new Error(`Invalid step ${step} of ${config.length}`));
    }

    const stepConfig = { ...config[step] };
    const elementSource = of(stepConfig.attachTo).pipe(
      switchMap(attach => typeof (attach) === 'function'
        ? of(null).pipe(
          delay(50),
          map(() => (<() => HTMLElement>attach)()),
          mergeMap(e => iif(() => !!e, of(e), throwError(new Error()))),
          retry(10),
        )
        : of(attach)),
    );

    return elementSource.pipe(
      map(e => {
        stepConfig.attachTo = e;
        return this.popoverService.createPopover(stepConfig);
      }),
      switchMap(ref => this.attachHandler(config, step, ref).pipe(
        startWith({ notClosed: true }),
        map(v => v && v.notClosed ? ref : v)
      )),
    );
  }

  private attachHandler(config: PopoverConfig[], currentStep: number, ref: PopoverRef) {
    this.transitionEnd
      .pipe(
        takeUntil(ref.closed),
        tap(() => ref.close()),
      )
      .subscribe();

    return ref.closed.pipe(
      switchMap(result => {
        if (result === 'back') {
          return this.openPopover(config, currentStep - 1);
        }

        if (result === 'next') {
          return this.openPopover(config, currentStep + 1);
        }

        return of(result);
      })
    );
  }
}
